import { useRef, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { api, errorMessage } from "../services/api";
import { useApiData } from "../hooks/useApiData";
import { useSession } from "../context/SessionContext";
import { DataState } from "./Feedback";
import StatusChip from "./StatusChip";
import { AskBox, ONE_EXAMPLES } from "./AskContractsCard";

export const TERM_LABELS = {
  baa: "Business associate agreement (BAA)",
  breach_notice: "Breach notification",
  term_renewal: "Term and renewal",
  termination: "Termination rights",
  data_training: "AI training on our data",
  subprocessors: "Subprocessors",
  data_location: "Data location",
  exit_data: "Data return and deletion",
  model_changes: "Changes to AI models or features",
  liability: "Liability",
  security_reports: "Security reports",
};
const ASSESSMENT = {
  OK: { label: "Acceptable", color: "#059669" },
  CONCERN: { label: "Check this", color: "#DC2626" },
  UNKNOWN: { label: "Not stated", color: "#94A3B8" },
};

/** The AI's JSON, or null when it can't be read (the model is told to send JSON but could slip). */
export function parseAi(text) {
  try {
    const value = JSON.parse(text);
    return value && typeof value === "object" ? value : null;
  } catch {
    return null;
  }
}

function Terms({ contract }) {
  const result = parseAi(contract.termsJson);
  if (!result || !Array.isArray(result.terms)) {
    return <Alert severity="warning">The AI's result for this contract could not be displayed. Try Analyze again.</Alert>;
  }
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      {result.summary && <Typography>{result.summary}</Typography>}
      {result.terms.map((t) => {
        const a = ASSESSMENT[t.assessment] || ASSESSMENT.UNKNOWN;
        return (
          <Box key={t.key} sx={{ p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700 }}>{TERM_LABELS[t.key] || t.key}</Typography>
              <StatusChip label={a.label} color={a.color} />
            </Box>
            <Typography variant="body2">{t.answer}</Typography>
            {t.quote && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, pl: 1.5, borderLeft: "3px solid", borderColor: "divider", fontStyle: "italic" }}>
                “{t.quote}”
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default function ContractsDialog({ vendor, onClose }) {
  const { me } = useSession();
  const state = useApiData(() => api.getContracts(vendor.id), [vendor.id]);
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(null); // "upload" or a contract id being analyzed
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  async function run(label, action) {
    setBusy(label);
    setError(null);
    try {
      return await action();
    } catch (e) {
      setError(errorMessage(e));
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function upload(file) {
    if (!file) return;
    const saved = await run("upload", () => api.uploadContract(vendor.id, file));
    if (saved) {
      state.reload();
      setExpanded(saved.id);
    }
  }
  async function analyze(c) {
    const saved = await run(c.id, () => api.analyzeContract(c.id));
    if (saved) {
      state.reload();
      setExpanded(c.id);
    }
  }
  // The window is opened first (a browser blocks pop-ups opened after waiting), then pointed at the temporary link.
  async function openDocument(c) {
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    const result = await run(`open-${c.id}`, () => api.getContractUrl(c.id));
    if (result && result.url) {
      if (tab) tab.location.href = result.url;
      else window.location.assign(result.url);
    } else if (tab) {
      tab.close();
    }
  }
  async function remove(c) {
    const ok = await run(c.id, async () => {
      await api.deleteContract(c.id);
      return true;
    });
    if (ok) state.reload();
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0.5 }}>{vendor.name} · contracts</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Upload an agreement (PDF, .txt or .md, up to 10 MB). The AI reads it and lists the key terms with the exact words it relied on.
        </Typography>
        <Alert severity="info" icon={<AutoAwesomeRoundedIcon fontSize="inherit" />} sx={{ mb: 2 }}>
          AI-generated from the uploaded text. It can miss or misread a clause, so check each answer against its quote before relying on it.
        </Alert>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {me.canWrite && (
          <Box sx={{ mb: 2 }}>
            <input ref={fileInput} type="file" hidden accept=".pdf,.txt,.md,application/pdf,text/plain"
              onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
            <Button variant="contained" startIcon={busy === "upload" ? <CircularProgress size={16} color="inherit" /> : <UploadFileRoundedIcon />}
              disabled={Boolean(busy)} onClick={() => fileInput.current.click()}>
              {busy === "upload" ? "Uploading…" : "Upload contract"}
            </Button>
          </Box>
        )}

        <DataState state={state}>
          {(rows) =>
            rows.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>No contracts uploaded for this vendor yet.</Typography>
            ) : (
              rows.map((c) => (
                <Accordion key={c.id} expanded={expanded === c.id} onChange={(e, open) => setExpanded(open ? c.id : null)} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", width: "100%", pr: 1 }}>
                      <Typography sx={{ fontWeight: 700 }}>{c.fileName}</Typography>
                      <Chip size="small" color={c.analyzed ? "success" : "default"} label={c.analyzed ? "Analyzed" : "Not analyzed yet"} />
                      {c.stored && <Chip size="small" variant="outlined" label="Stored in S3" />}
                      <Typography variant="caption" color="text.secondary">
                        Uploaded {(c.uploadedOn || "").slice(0, 10)}{c.uploadedBy ? ` by ${c.uploadedBy}` : ""}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                      {c.stored && (
                        <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => openDocument(c)}
                          startIcon={busy === `open-${c.id}` ? <CircularProgress size={14} /> : <OpenInNewRoundedIcon />}>
                          Open document
                        </Button>
                      )}
                      {me.canWrite && (<>
                        <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => analyze(c)}
                          startIcon={busy === c.id ? <CircularProgress size={14} /> : <AutoAwesomeRoundedIcon />}>
                          {busy === c.id ? "Reading the contract…" : c.analyzed ? "Analyze again" : "Analyze with AI"}
                        </Button>
                        <Button size="small" color="error" disabled={Boolean(busy)} onClick={() => remove(c)}>Delete</Button>
                      </>)}
                    </Box>
                    {c.analyzed ? <Terms contract={c} /> : <Typography color="text.secondary">Press “Analyze with AI” to extract the key terms.</Typography>}
                    <Box sx={{ mt: 2.5, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                      <Typography sx={{ fontWeight: 700, mb: 1 }}>Ask a question about this contract</Typography>
                      <AskBox scope={{ contractId: c.id }} examples={ONE_EXAMPLES} placeholder="For example: can the vendor train its AI on our data?" />
                    </Box>
                    {c.analyzed && c.model && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                        Read by {c.model} on {(c.analyzedOn || "").slice(0, 10)}.
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))
            )
          }
        </DataState>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={Boolean(busy)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
